import {observable} from 'mobx';
import {Context, EMPTY_CONTEXT, Options, Schema} from './schema';

export type EntriesSchema<T> = Schema<T>;

export interface MapOptions<T>
  extends Options<Record<string, T | null> | null> {
  minSize?: number | ((context?: Context) => number | null);
  maxSize?: number | ((context?: Context) => number | null);
}

export class MapSchema<
  T = any,
  S extends EntriesSchema<T> = EntriesSchema<T>
> extends Schema<Record<string, T | null> | null, MapOptions<T>> {
  private _entriesSchema: S;

  constructor(
    // Force type parameter `T` to be inferred from the entries' schema
    entriesSchema: S & EntriesSchema<T>,
    options: MapOptions<T> = {},
  ) {
    super(options);
    this._entriesSchema = entriesSchema;
  }

  public toJSON(): object {
    return {
      type: 'MapSchema',
      ...super.toJSON(),
      minSize: this._options.minSize,
      maxSize: this._options.maxSize,
      entriesSchema: this._entriesSchema,
    };
  }

  public get entriesSchema(): S {
    return this._entriesSchema;
  }

  // When defined, merge each of schema's initial value entries with their
  // respective initial value
  public initialValue(
    context: Context = EMPTY_CONTEXT,
  ): Record<string, T | null> | null {
    const initialValue = super.initialValue(context);
    return initialValue === null
      ? null
      : // Although the order of the entries should not be relied upon, an
        // entry's initial value may access the initial values of entries
        // defined before it
        Object.keys(initialValue).reduce((obj, key) => {
          obj[key] = this._merge(
            this.entryInitialValue(obj, key, context),
            initialValue[key],
          );
          return obj;
        }, {});
  }

  // Initial value for entry with key `key` of `value`
  public entryInitialValue(
    value: Record<string, T | null>,
    key: string,
    context: Context = EMPTY_CONTEXT,
  ): T | null {
    return this._entriesSchema.initialValue(
      this._buildChildContext(value, key, context),
    ) as T;
  }

  public minSize(context: Context = EMPTY_CONTEXT): number | null {
    return this._options.minSize === undefined
      ? null
      : typeof this._options.minSize === 'function'
        ? this._options.minSize(context)
        : this._options.minSize;
  }

  public maxSize(context: Context = EMPTY_CONTEXT): number | null {
    return this._options.maxSize === undefined
      ? null
      : typeof this._options.maxSize === 'function'
        ? this._options.maxSize(context)
        : this._options.maxSize;
  }

  public valueToMobx(
    value: Record<string, T | null> | null,
    context: Context = EMPTY_CONTEXT,
  ): Record<string, T | null> | null {
    const map = this._nullifyValue(value);
    if (map === null) {
      return null;
    }
    if (this._entriesSchema.isComputed) {
      throw new Error(
        'Computed values not supported as direct children of maps',
      );
    }
    // Return a MobX map
    const mobxMap: any = observable.shallowMap();
    mobxMap.replace(
      Object.keys(map).reduce((obj, key) => {
        obj[key] = this._entriesSchema.valueToMobx(
          map[key],
          this._buildChildContext(mobxMap, key, context),
        );
        return obj;
      }, {}),
    );
    return mobxMap;
  }

  public entryToMobx(
    value: Record<string, T | null>,
    key: string,
    entryValue: any,
    context: Context = EMPTY_CONTEXT,
  ): any {
    return this._entriesSchema.valueToMobx(
      entryValue,
      this._buildChildContext(value, key, context),
    );
  }

  public valueToJS(
    value: Record<string, T | null> | null,
    context: Context = EMPTY_CONTEXT,
  ): Record<string, T | null> | null {
    const map = this.isComputed
      ? this.computedValue(context)
      : this._nullifyValue(value);
    if (map === null) {
      return null;
    }
    // When all entries are computed, return a lazy map with "getters" for each
    // entry
    if (this._entriesSchema.isComputed) {
      const lazyMap: Record<string, T | null> = {};
      for (const key of Object.keys(map)) {
        Object.defineProperty(lazyMap, key, {
          enumerable: true,
          configurable: true,
          get: () =>
            this._entriesSchema.valueToJS(
              null,
              this._buildChildContext(lazyMap, key, context),
            ),
        });
      }
      return lazyMap;
    }
    // Return a copy of the map with `valueToJS` called for each entry
    return Object.keys(map).reduce((copyMap, key) => {
      copyMap[key] = this._entriesSchema.valueToJS(
        map[key],
        this._buildChildContext(copyMap, key, context),
      );
      return copyMap;
    }, {});
  }

  public typeIsValid(
    value: any,
    strict?: boolean,
  ): value is Record<string, T | null> | null {
    return (
      value === null ||
      (typeof value === 'object' &&
        !Array.isArray(value) &&
        Object.keys(value).every(key =>
          this._entriesSchema.typeIsValid(value[key], strict),
        ))
    );
  }

  public valueIsValid(
    value: Record<string, T | null> | null,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    return (
      this.requiredIsValid(value, context) &&
      this.minSizeIsValid(value, context) &&
      this.maxSizeIsValid(value, context) &&
      this.entriesAreValid(value, context) &&
      this.customFunctionIsValid(value, context)
    );
  }

  public minSizeIsValid(
    value: Record<string, T | null> | null,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    const minSize = this.minSize(context);
    return (
      value === null || minSize === null || Object.keys(value).length >= minSize
    );
  }

  public maxSizeIsValid(
    value: Record<string, T | null> | null,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    const maxSize = this.maxSize(context);
    return (
      value === null || maxSize === null || Object.keys(value).length <= maxSize
    );
  }

  public entryIsValid(
    value: Record<string, T | null>,
    key: string,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    return this._entriesSchema.valueIsValid(
      value[key],
      this._buildChildContext(value, key, context),
    );
  }

  public entriesAreValid(
    value: Record<string, T | null> | null,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    return (
      value === null ||
      Object.keys(value).every(key => this.entryIsValid(value, key, context))
    );
  }
}
